import { useState } from "react";
import {
  Activity,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Home,
  Hourglass,
  LayoutDashboard,
  Minus,
  Plus,
  TrendingUp,
  Upload,
  Wallet,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { HospitalFinancialIndicators } from "../components/hospital-dashboard/HospitalFinancialIndicators";
import { HospitalInvoiceDistributionChart } from "../components/hospital-dashboard/HospitalInvoiceDistributionChart";
import { HospitalDashboardClientsSection } from "../components/hospital-dashboard/HospitalDashboardClientsSection";
import { DashboardMetricCard } from "../components/dashboard/DashboardMetricCard";
import { KpiGrid, type KpiTone } from "../components/KpiCard";
import {
  DashboardQuickAccess,
  type DashboardQuickAccessConfig,
} from "../components/dashboard/DashboardQuickAccess";
import { useHospitalDashboard } from "../hooks/useHospitalDashboard";
import { getRoleStatRoute } from "../navigation/dashboardRoutes";

const indicatorIcons = {
  file: FileText,
  upload: Upload,
  wallet: Wallet,
  clock: Clock,
  check: CheckCircle,
  x: XCircle,
  dollar: DollarSign,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  dollar: DollarSign,
  activity: Activity,
  trending: TrendingUp,
  x: XCircle,
};

const hospitalMetricConfig: Record<string, KpiTone> = {
  invoices: "sky",
  submitted: "amber",
  paid: "lime",
  review: "purple",
  approved: "mint",
  rejected: "red",
  revenue: "pink",
};

const hospitalQuickAccessConfig: Record<string, DashboardQuickAccessConfig> = {
  admission: { icon: Plus, tone: "green" },
  submit: { icon: Upload, tone: "blue" },
  invoices: { icon: FileText, tone: "purple" },
  admissions: { icon: Home, tone: "orange" },
};

export function HospitalDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.hospitalBoard;
  const { data, loading, error, usingApi } = useHospitalDashboard();
  const [activeQuickAccess, setActiveQuickAccess] = useState("admission");

  const subtitle = labels.subtitle
    .replace("{invoices}", String(data.header.totalInvoices))
    .replace("{revenue}", data.header.monthlyRevenue);

  const quickAccessItems = data.quickActions.map((action) => ({
    id: action.id,
    href: action.href,
    label: labels.quickActions[action.id as keyof typeof labels.quickActions],
  }));

  return (
    <div className="space-y-5">
      {loading && <p className="text-sm text-muted">Chargement du tableau de bord…</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
      {usingApi && !loading && (
        <p className="text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
      )}

      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={subtitle}
        icon={LayoutDashboard}
        badge={data.header.providerBadge}
        date={data.header.displayDate}
      />

      <div>
        <DashboardSectionLabel>{labels.sections.indicators}</DashboardSectionLabel>
        <KpiGrid className="grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {data.indicators.map((indicator) => {
            const Icon = indicatorIcons[indicator.icon];
            const FooterIcon = footerIcons[indicator.footerIcon];
            const label = labels.indicators[indicator.id as keyof typeof labels.indicators];
            const footer =
              labels.indicatorFooters[indicator.footerKey as keyof typeof labels.indicatorFooters];
            const tone = hospitalMetricConfig[indicator.id] ?? "slate";

            return (
              <DashboardMetricCard
                key={indicator.id}
                title={label}
                value={indicator.value}
                footer={
                  <span className="inline-flex items-center gap-1">
                    <FooterIcon size={12} />
                    {footer}
                  </span>
                }
                icon={Icon}
                href={getRoleStatRoute("hospital", indicator.id)}
                tone={tone}
              />
            );
          })}
        </KpiGrid>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.quickAccess}</DashboardSectionLabel>
        <DashboardQuickAccess
          items={quickAccessItems}
          configById={hospitalQuickAccessConfig}
          activeId={activeQuickAccess}
          onSelect={setActiveQuickAccess}
        />
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.charts}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <HospitalInvoiceDistributionChart data={data} />
          <HospitalFinancialIndicators data={data} />
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.clients}</DashboardSectionLabel>
        <HospitalDashboardClientsSection data={data} />
      </div>
    </div>
  );
}
